import { styled } from '@mui/material';
import { useEffect, useState } from 'react';
import { fetchWrapper } from '@/lib/fetch-wrapper';
import { ROUTES, urls } from '@/urls';
import { formatCurrencyNoUnit } from '@/lib/formatters';
import { Tone } from '@/theme/tokens';
import { KpiCard, StatRow, DsIconName } from '@/components/ds';
import type { CostsSummary as CostsSummaryType } from '@cloudcost/shared-types/costs';

// The costs summary endpoint drives the four dashboard KPI cards. All values are
// DERIVED from the same `costssummary` payload that this screen already fetches —
// no new endpoints. The `messages` link map (click → BUDGETS / OPTIMIZATION) is
// preserved via the per-card `url`.

const num = (
  box: { data: Record<string, { value: number; unit: string }> } | undefined,
  key: string,
): number => box?.data?.[key]?.value ?? 0;

// The backend can additionally emit a 'warning' status; widen to string so the
// comparisons below type-check against the narrower declared union.
const statusToTone = (status: string): Tone =>
  status === 'error' ? 'danger' : status === 'warning' ? 'warn' : 'success';

interface KpiDef {
  key: string;
  label: string;
  icon: DsIconName;
  tone: Tone;
  value: string;
  delta?: string;
  deltaTone: Tone;
  sub: string;
  url: string;
}

function CostsSummary() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CostsSummaryType>({} as CostsSummaryType);

  useEffect(() => {
    setLoading(true);
    fetchWrapper
      .get(urls.DevOps.costsSummary)
      .then(setData)
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const first = data?.firstBoxData;
  const second = data?.secondBoxData;
  const third = data?.thirdBoxData;

  const firstStatus = (first?.status as string) ?? 'success';
  const secondStatus = (second?.status as string) ?? 'success';
  const thirdStatus = (third?.status as string) ?? 'success';

  const spending = num(first, 'Spending');
  const budget = num(first, 'Global Budget') || num(first, 'Global budget');
  const forecast = num(first, 'Forecast');
  const savingPotential = num(second, 'Identified saving potential');
  const implemented = num(second, 'Implemented saving');
  const costIssues = third
    ? Object.values(third.data ?? {}).reduce(
        (sum, item) => sum + (item?.value ?? 0),
        0,
      )
    : 0;

  const budgetUsedPct = budget > 0 ? Math.round((spending / budget) * 100) : null;
  const remaining = forecast - spending;

  const budgetDelta =
    budgetUsedPct == null
      ? 'Set a budget'
      : firstStatus === 'error'
        ? 'Over forecast'
        : firstStatus === 'warning'
          ? 'No budget'
          : 'On track';

  const issuesDelta =
    thirdStatus === 'error'
      ? 'Needs action'
      : thirdStatus === 'warning'
        ? 'Set budgets'
        : 'All clear';

  const cards: KpiDef[] = [
    {
      key: 'spend',
      label: 'Monthly spend',
      icon: 'dollar',
      tone: 'primary',
      value: formatCurrencyNoUnit(spending),
      delta:
        remaining > 0 ? `+${formatCurrencyNoUnit(remaining)}` : 'On forecast',
      deltaTone: firstStatus === 'error' ? 'danger' : 'neutral',
      sub: 'to forecast',
      url: ROUTES.BUDGETS,
    },
    {
      key: 'budget',
      label: 'Budget used',
      icon: 'shield',
      tone: 'success',
      value: budgetUsedPct != null ? `${budgetUsedPct}%` : '—',
      delta: budgetDelta,
      deltaTone: budgetUsedPct == null ? 'warn' : statusToTone(firstStatus),
      sub: budget > 0 ? `${formatCurrencyNoUnit(budget)} cap` : 'No global budget',
      url: ROUTES.BUDGETS,
    },
    {
      key: 'saving',
      label: 'Saving potential',
      icon: 'spark',
      tone: 'warn',
      value: formatCurrencyNoUnit(savingPotential),
      delta:
        implemented > 0
          ? `${formatCurrencyNoUnit(implemented)} saved`
          : secondStatus === 'error'
            ? 'Review'
            : 'On track',
      deltaTone: secondStatus === 'error' ? 'danger' : 'success',
      sub: 'per month',
      url: ROUTES.OPTIMIZATION,
    },
    {
      key: 'issues',
      label: 'Cost issues',
      icon: 'warn',
      tone: 'danger',
      value: String(costIssues),
      delta: issuesDelta,
      deltaTone: statusToTone(thirdStatus),
      sub: 'open',
      url: ROUTES.BUDGETS,
    },
  ];

  return (
    <StatRow columns={4}>
      {cards.map((c) => (
        <CardLink key={c.key} href={c.url}>
          <KpiCard
            label={c.label}
            value={loading ? '—' : c.value}
            icon={c.icon}
            tone={c.tone}
            delta={loading ? undefined : c.delta}
            deltaTone={c.deltaTone}
            sub={c.sub}
          />
        </CardLink>
      ))}
    </StatRow>
  );
}

// Preserves the original click-through navigation (plain anchor, same as before)
// while letting the KpiCard fill the grid cell.
const CardLink = styled('a')({
  display: 'block',
  textDecoration: 'none',
  color: 'inherit',
  transition: 'transform .15s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
  },
});

export default CostsSummary;
